import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useArchivePriorityReferralMutation, useGetReferralCountQuery, useGetReferralsQuery, useMoveReferralToPriorityEntryMutation, useRestorePriorityReferralMutation } from "../../graphql/graphql";
import { ReferralEmptyState } from "./ReferralEmptyState";
import { AutoHeightContainer, calculateOffset, doesArrayHaveData, TablePagination } from "../../shared/Internal";
import { Confirmation } from "../../shared/Confirmation";
import { PriorityReferralRow } from "./PriorityReferralRow";
import { ManagedTable } from "../../shared/ManagedTable";
import { useTherapproveDrawers } from "../../shared/TherapproveOverlays";
export const PriorityReferralView = ({ search = "", isActiveTab, archived = false, therapyCategoryIds }) => {
    // State
    const [page, setPage] = useState(1);
    const { patient } = useTherapproveDrawers();
    // Effects
    useEffect(() => {
        setPage(1);
    }, [search]);
    const [confirmationProps, setConfirmationProps] = useState(null);
    // Mutations
    const [{ fetching: moveReferralToPriorityEntryFetching }, moveReferralToPriorityEntryMutation] = useMoveReferralToPriorityEntryMutation();
    const [{ fetching: restorePriorityReferralFetching }, restorePriorityReferralMutation] = useRestorePriorityReferralMutation();
    const [{ fetching: archivePriorityReferralFetching }, archivePriorityReferralMutation] = useArchivePriorityReferralMutation();
    // Get the priority referrals.
    const [{ data, fetching }] = useGetReferralsQuery({
        requestPolicy: "cache-and-network",
        variables: {
            archived,
            search,
            offset: calculateOffset(page),
            therapyCategoryIds: therapyCategoryIds?.length ? therapyCategoryIds : undefined
        },
        pause: isActiveTab === false
    });
    const priorityReferrals = data?.priorityReferrals?.priorityReferrals ?? [];
    // Get the priority referral count.
    const [{ data: countData }] = useGetReferralCountQuery({
        variables: {
            archived,
            therapyCategoryIds: therapyCategoryIds?.length ? therapyCategoryIds : undefined
        },
        pause: isActiveTab === false
    });
    const count = countData?.priorityReferrals?.priorityReferralCount ?? 0;
    // Confirmation Modal
    const [confirmationModalOpened, { open: openConfirmationModal, close: closeConfirmationModal }] = useDisclosure(false);
    // Handlers
    const openPatientProfileDrawer = (patientId) => {
        patient.open({ patientId });
    };
    const moveToPriorityEntry = (priorityReferralId) => {
        setConfirmationProps({
            action: "move",
            handleAction: async () => {
                await moveReferralToPriorityEntryMutation({
                    priorityReferralId
                });
                closeConfirmationModal();
            }
        });
        openConfirmationModal();
    };
    const archiveRestorePriorityReferral = (priorityReferralId) => {
        if (!!archived) {
            setConfirmationProps({
                action: "restore",
                handleAction: async () => {
                    await restorePriorityReferralMutation({
                        priorityReferralId
                    });
                    closeConfirmationModal();
                }
            });
        }
        else {
            setConfirmationProps({
                action: "archive",
                handleAction: async () => {
                    await archivePriorityReferralMutation({
                        priorityReferralId
                    });
                    closeConfirmationModal();
                }
            });
        }
        openConfirmationModal();
    };
    /**
     * Create the table header
     */
    const tableHead = (_jsxs(Table.Tr, { children: [_jsx(Table.Th, { style: { paddingLeft: "0.5rem" }, children: "Primary Contact" }), _jsx(Table.Th, { children: "Contact Info" }), _jsx(Table.Th, { children: "Client" }), _jsx(Table.Th, { children: "Therapy Needed" }), _jsx(Table.Th, { children: "Location" }), _jsx(Table.Th, { children: "Wait Time" }), _jsx(Table.Th, {})] }));
    // Priority Referral Rows
    const priorityReferralRows = useMemo(() => {
        if (!doesArrayHaveData(priorityReferrals))
            return [];
        return priorityReferrals.map((priorityReferral) => {
            return (_jsx(PriorityReferralRow, { priorityReferral: priorityReferral, movePriorityReferralToPriorityEntry: moveToPriorityEntry, archiveRestorePriorityReferral: archiveRestorePriorityReferral, modalHandlers: {
                    patientProfileDrawerOpen: openPatientProfileDrawer
                } }, priorityReferral?.priorityReferralId));
        });
    }, [priorityReferrals]);
    const caption = _jsx(TablePagination, { count: count, page: page, setPage: setPage });
    return (_jsxs(AutoHeightContainer, { children: [_jsx(ManagedTable, { withBorder: true, header: tableHead, rows: priorityReferralRows, caption: caption, minWidth: 800, fetching: fetching, emptyState: _jsx(ReferralEmptyState, {}) }), confirmationProps && (_jsx(Confirmation, { opened: confirmationModalOpened, close: closeConfirmationModal, action: confirmationProps.action, handleAction: confirmationProps.handleAction, isLoading: moveReferralToPriorityEntryFetching ||
                    restorePriorityReferralFetching ||
                    archivePriorityReferralFetching }))] }));
};
